import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";

const ChangePassword = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        if (!currentPassword || !newPassword || !confirmPassword) {
            setError("Please fill in all fields.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("New password and confirm password do not match.");
            return;
        }
        if ((newPassword || "").length < 6) {
            setError("New password must be at least 6 characters long.");
            return;
        }

        setLoading(true);
        try {
            const primaryBase = "https://us-central1-ecohomely-app.cloudfunctions.net/app";
            const fallbackBase = "https://us-central1-workexpress-1ffff.cloudfunctions.net/app";
            let res = await fetch(`${primaryBase}/admin/change-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: String(user?.username || ""), currentPassword: String(currentPassword), newPassword: String(newPassword) })
            });
            if (res.status === 404) {
                // Retry against fallback base if primary returns 404
                res = await fetch(`${fallbackBase}/admin/change-password`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ username: String(user?.username || ""), currentPassword: String(currentPassword), newPassword: String(newPassword) })
                });
            }
            const contentType = res.headers.get("content-type") || "";
            const isJson = contentType.includes("application/json");
            const payload = isJson ? await res.json() : { message: await res.text() };
            if (!res.ok) {
                throw new Error(payload?.message || payload?.error || `Request failed (${res.status})`);
            }
            setMessage(payload?.message || "Password updated successfully.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            // Auto logout shortly after success
            setTimeout(async () => {
                try {
                    await logout();
                } catch (e) { }
                // Use navigate to ensure React Router handles redirection cleanly
                navigate("/", { replace: true });
            }, 1200);
        } catch (err) {
            setError(err.message || "Failed to change password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="panel-container">
            <div className="panel-header">
                <h2>Change Password</h2>
            </div>
            <form className="form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <label>Current Password</label>
                    <div className="pwd-field">
                        <input type={showCurrent ? "text" : "password"} value={String(currentPassword)} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" />
                        <button type="button" className="eye-btn" onClick={() => setShowCurrent(!showCurrent)} aria-label={showCurrent ? "Hide" : "Show"}>
                            {showCurrent ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>
                </div>
                <div className="form-row">
                    <label>New Password</label>
                    <div className="pwd-field">
                        <input type={showNew ? "text" : "password"} value={String(newPassword)} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" />
                        <button type="button" className="eye-btn" onClick={() => setShowNew(!showNew)} aria-label={showNew ? "Hide" : "Show"}>
                            {showNew ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>
                </div>
                <div className="form-row">
                    <label>Confirm New Password</label>
                    <div className="pwd-field">
                        <input type={showConfirm ? "text" : "password"} value={String(confirmPassword)} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" />
                        <button type="button" className="eye-btn" onClick={() => setShowConfirm(!showConfirm)} aria-label={showConfirm ? "Hide" : "Show"}>
                            {showConfirm ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>
                </div>
                {error && <div className="alert error">{error}</div>}
                {message && <div className="alert success">{message}</div>}
                <button className="primary-btn" type="submit" disabled={loading}>
                    {loading ? "Updating..." : "Update Password"}
                </button>
            </form>

            <style>{`
        .panel-container { background:var(--bg-card, #fff); border-radius:16px; padding:24px; box-shadow:0 6px 15px var(--shadow, rgba(0,0,0,0.08)); }
        .panel-header { border-bottom:1px solid var(--border-color, #e5e7eb); margin-bottom:16px; padding-bottom:8px; }
        .panel-header h2 { color:var(--text-primary, #1f2937); }
        .form { display:flex; flex-direction:column; gap:14px; max-width:420px; }
        .form-row { display:flex; flex-direction:column; gap:6px; }
        .form-row label { font-weight:600; color:var(--text-primary, #374151); }
        .form-row input { padding:10px 12px; border:1px solid var(--border-color, #d1d5db); border-radius:6px; font-size:14px; background:var(--bg-input, #ffffff); color:var(--text-primary, #111827); }
        .form-row input:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.15); }
        .pwd-field { position:relative; display:flex; align-items:center; }
        .pwd-field input { width:100%; padding-right:40px; }
        .eye-btn { position:absolute; right:8px; background:transparent; border:none; width:34px; height:34px; border-radius:6px; display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-primary, #374151); }
        .eye-btn:hover { background:var(--bg-hover, rgba(0,0,0,0.05)); }
        .primary-btn { padding:10px 16px; border:1px solid #3b82f6; border-radius:6px; background:#3b82f6; color:#fff; font-weight:600; cursor:pointer; }
        .primary-btn:disabled { opacity:0.7; cursor:not-allowed; }
        .primary-btn:hover:not(:disabled) { background:#2563eb; border-color:#2563eb; }
        .alert { padding:10px 12px; border-radius:6px; font-size:14px; }
        .alert.error { background:#fef2f2; color:#b91c1c; border:1px solid #fecaca; }
        .alert.success { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
      `}</style>
        </div>
    );
};

export default ChangePassword;


